import { useContext, useEffect, useMemo, useState } from 'react';

import { UserDetailContext, UserDetailContextProps } from '../../../../context/userDetail/userDetailContext';
import Charts, { ViewMode } from './Charts';
import Sidebar, { PeriodValue } from './Sidebar';
import { ConsumptionEntry, getConsumptionData, loadHalfHourlyConsumptionData } from '../../services/dataService';
import { EnergyUsageWrapper } from '../../energyusage.styles';
import { Loading, MobileActionsPanel, SideMenuFilter, StyledTitle } from '../../../../common/components';
import { useAuthState } from '../../../../providers/authProvider';
import { MetersFilterObject } from '../../../../common/api/meterReadsApi';
import { SelectDateResult } from '../../../../common/components/DateFilterModal';
import { MpanFilterItem, PickedMonthDate } from './types/ChartTypes';
import { getDateRange } from '../../utils/dates';
import { getKey } from '../../utils/key';
import { canDisplayHHDataFromMpans, containsNonHHDataInMpans, loadMpans } from '../../services/mpanService';
import SidebarMobile from './SidebarMobile';
import { GlobalContext, GlobalDetailContextProps } from '../../../../context/globalContext/globalContext';
import { SidebarMobileWrapper } from './energyusage.styles';
import { NotificationBox } from '../../../health/NotificationBox/NotificationBox';
import { downloadCSV } from '../../../../helpers/downloadCSV';
import { downloadXML } from '../../../../helpers/downloadXML';
import { downloadXLSX } from '../../../../helpers/downloadXLSX';
import { convertDataToReport, formatHHData } from '../../services/exportService';
import { DATE_FORMAT, REVERSED_DATE_FORMAT_HYPHEN } from '../../../../common/constants';
import * as colors from '../../../../common/styles/colors';
import iconSet from '../../../../assets/selection.json';

import IcomoonReact from 'icomoon-react';
import moment from 'moment';
import { dataLayerPageEvents } from '../../../utils/analytics/analytics';
import { TagManager } from '../../../utils/analytics/TagManager';
import { isTpiUser } from '../../../../helpers/tpiUser';

function EnergyUsage() {
  const authContext = useAuthState();
  const globalContext = useContext<GlobalDetailContextProps>(GlobalContext);
  const { userDetail, setUserDetail } = useContext<UserDetailContextProps>(UserDetailContext);

  const [mpanPeriodState, setMpanPeriodState] = useState<{ period: string; mpans: MpanFilterItem[] }>({
    period: '',
    mpans: [],
  });
  const [view, setView] = useState<ViewMode>('monthly');
  const [yearMonth, setYearMonth] = useState<string | undefined>(undefined);
  const [hhDate, setHHDate] = useState<string | undefined>(undefined);
  const [selectedCustomer, setSelectedCustomer] = useState<string>('');
  const [mpansForSelectedCustomer, setMpansForSelectedCustomer] = useState<MpanFilterItem[]>([]);
  const [isShowingSmartMeterModal, setIsShowingSmartMeterModal] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [validData, setValidData] = useState(false);

  // Data states
  const [customerMonthlyData, setCustomerMonthlyData] = useState<ConsumptionEntry[]>([]);
  const [monthlySeries, setMonthlySeries] = useState<Record<string, any[]> | null>(null);
  // Cache for loaded MPAN monthly data
  const [monthlyCache, setMonthlyCache] = useState<Record<
    string,
    { type: Api.HHRequestType; consumptions: any[] }
  > | null>(null);
  const [dailySeries, setDailySeries] = useState<Record<string, any[]> | null>(null);
  const [dailyCache, setDailyCache] = useState<Record<string, { type: Api.HHRequestType; consumptions: any[] }> | null>(
    null
  );
  const [hhSeries, setHhSeries] = useState<Record<string, any[]> | null>(null);
  const [hhCache, setHhCache] = useState<Record<string, { type: Api.HHRequestType; consumptions: any[] }> | null>(null);

  const [customDateRange, setCustomDateRange] = useState<PickedMonthDate | undefined>(undefined);
  const [mpansContainHH, setMpansContainHH] = useState<boolean>(true);
  const [selectedMpansContainNonHH, setSelectedMpansContainNonHH] = useState<boolean>(false);

  const [selectedViewByIndex, setSelectedViewByIndex] = useState<number>(1);

  const viewByOptions = [
    { value: '24', display: 'Last 24 Months' },
    { value: '12', display: 'Last 12 Months' },
    { value: '6', display: 'Last 6 Months' },
  ];

  // Load customer monthly data
  useEffect(() => {
    loadCustomerData();

    const containsHHData = canDisplayHHDataFromMpans(mpanPeriodState.mpans);
    setMpansContainHH(containsHHData);

    const containsNonHHData = containsNonHHDataInMpans(mpanPeriodState.mpans);
    setSelectedMpansContainNonHH(containsNonHHData);

    const viewByIndex = viewByOptions.findIndex(option => option.value === mpanPeriodState.period);
    setSelectedViewByIndex(viewByIndex >= 0 ? viewByIndex : 1);
  }, [selectedCustomer, mpanPeriodState.period, mpanPeriodState.mpans]);

  // Load MPAN monthly/daily/halfhourly data
  useEffect(() => {
    loadMpanData();
  }, [mpanPeriodState, view, yearMonth, hhDate, selectedCustomer]);

  const loadCustomerData = async () => {
    if (selectedCustomer && mpanPeriodState.period && mpanPeriodState.mpans.length === 0) {
      // Get date range from period selection
      let date = getDateRange(mpanPeriodState.period);

      setIsLoading(true);

      setValidData(false);

      const response = await getConsumptionData({
        url: process.env.REACT_APP_API_URL,
        context: authContext,
        entityId: selectedCustomer,
        startDate: date.begin,
        endDate: date.end,
        groupingInterval: 'Month',
        entityType: 'Customer',
        customerId: selectedCustomer,
        requestType: 'Customer',
      });
      setIsLoading(false);

      if (!response || !response.consumptions) {
        setCustomerMonthlyData([]);
        return;
      }

      setValidData(true);
      setCustomerMonthlyData(response.consumptions);
    } else {
      setCustomerMonthlyData([]);
      return;
    }
  };

  const loadMpanData = async () => {
    const mpans = mpanPeriodState.mpans;
    const period = mpanPeriodState.period;
    if (!mpans.length || !period) return;
    if (view === 'monthly') {
      const date = getDateRange(period);
      // Use cache to avoid duplicate loads
      const cache = monthlyCache || {};
      const newSeries: Record<string, { type: Api.HHRequestType; consumptions: any[] }> = { ...cache };

      setIsLoading(true);

      const mpansToLoad = mpans.filter(mpanDetails => {
        const key = getKey(mpanDetails.mpan, date);
        return !cache[key];
      });

      setValidData(false);

      for (const mpan of mpansToLoad) {
        const response = await getConsumptionData({
          url: process.env.REACT_APP_API_URL,
          context: authContext,
          period: period,
          entityId: mpan.mpan,
          startDate: date.begin,
          endDate: date.end,
          groupingInterval: 'Month',
          entityType: 'MpanCore',
          customerId: selectedCustomer,
          requestType: mpan.requestType,
        });
        const type = mpan.requestType;

        if (response && response.consumptions && response.consumptions.length > 0) {
          const key = getKey(mpan.mpan, date);
          newSeries[key] = { type, consumptions: response.consumptions };
        }
      }
      setIsLoading(false);

      const series = mpans.reduce((acc, mpan) => {
        const key = getKey(mpan.mpan, date);
        acc[mpan.mpan] = newSeries[key]?.consumptions || [];
        return acc;
      }, {} as Record<string, any[]>);

      if (
        Object.keys(series).length > 0 &&
        Object.values(series).some(arr => arr.some(item => item.totalConsumption > 0))
      ) {
        setValidData(true);
      }

      setMonthlySeries(series);
      setMonthlyCache(newSeries);
    } else if (view === 'daily') {
      const date = getDateRange('daily', customDateRange);
      const cache = dailyCache || {};
      const newSeries: Record<string, { type: Api.HHRequestType; consumptions: any[] }> = { ...cache };

      const mpansToLoad = mpans.filter(mpanDetails => {
        const key = getKey(mpanDetails.mpan, date);
        return !cache[key];
      });

      setIsLoading(true);

      setValidData(false);

      for (const mpan of mpansToLoad) {
        const response = await getConsumptionData({
          url: process.env.REACT_APP_API_URL,
          context: authContext,
          period: period,
          entityId: mpan.mpan,
          startDate: date.begin,
          endDate: date.end,
          groupingInterval: 'Day',
          entityType: 'MpanCore',
          customerId: selectedCustomer,
          requestType: mpan.requestType,
        });
        const type = mpan.requestType;

        const key = getKey(mpan.mpan, date);

        if (response && response.consumptions && response.consumptions.length > 0) {
          newSeries[key] = { type, consumptions: response.consumptions };
        }
      }
      setIsLoading(false);

      const series = mpans.reduce((acc, mpan) => {
        const key = getKey(mpan.mpan, date);
        acc[mpan.mpan] = newSeries[key]?.consumptions || [];
        return acc;
      }, {} as Record<string, any[]>);

      if (
        Object.keys(series).length > 0 &&
        Object.values(series).some(arr => arr.some(item => item.totalConsumption > 0))
      ) {
        setValidData(true);
      }

      setDailySeries(series);
      setDailyCache(newSeries);
    } else if (view === 'halfhourly') {
      const date = getDateRange('halfhourly', customDateRange);
      const cache = hhCache || {};
      const newSeries: Record<string, { type: Api.HHRequestType; consumptions: any[] }> = { ...cache };

      setIsLoading(true);

      const mpansToLoad = mpans.filter(mpanDetails => {
        const key = getKey(mpanDetails.mpan, date);
        return !cache[key];
      });

      setValidData(false);

      for (const mpan of mpansToLoad) {
        const response = await loadHalfHourlyConsumptionData({
          url: process.env.REACT_APP_API_URL,
          context: authContext,
          entityId: mpan.mpan,
          startDate: date.begin,
          endDate: date.end,
          entityType: 'MpanCore',
          customerId: selectedCustomer,
          requestType: mpan.requestType,
        });
        const type = mpan.requestType;

        const key = getKey(mpan.mpan, date);
        if (response && response.consumptions && response.consumptions.length > 0) {
          newSeries[key] = { type, consumptions: response.consumptions[0]?.periods ?? [] };
        }
      }
      setIsLoading(false);

      const series = mpans.reduce((acc, mpan) => {
        const key = getKey(mpan.mpan, date);
        acc[mpan.mpan] = newSeries[key]?.consumptions || [];
        return acc;
      }, {} as Record<string, any[]>);

      if (
        Object.keys(series).length > 0 &&
        Object.values(series).some(arr => arr.some(item => item.consumptionValue > 0))
      ) {
        setValidData(true);
      }

      setHhSeries(series);
      setHhCache(newSeries);
    }
  };

  // Drilldown event handlers
  const handleMonthClick = (ym: string) => {
    setYearMonth(ym);

    const [year, month] = ym.split('-');
    const begin = `${year}-${month}-01T00:00:00`;

    const endDateObj = new Date(Number(year), Number(month), 0);
    const end = `${endDateObj.getFullYear()}-${month}-${endDateObj.getDate()}T00:00:00`;

    setCustomDateRange({ begin, end });
    setView('daily');
  };

  const handleDayClick = (d: string) => {
    setHHDate(d);
    // Calculate date range for selected day
    const begin = `${d}T00:00:00`;
    // End is the same day at 23:59:59 UTC
    const end = `${d}T23:59:59Z`;
    setCustomDateRange({ begin, end });
    setView('halfhourly');
  };

  const handlePeriodChange = (periodValue: PeriodValue) => {
    setView('monthly');
    setMpanPeriodState(prev => ({ ...prev, period: periodValue }));
    setCustomDateRange(undefined);
    setMonthlyCache(null);
    setDailyCache(null);
    setHhCache(null);
  };

  const moveToDaily = () => {
    setView('daily');
    // Restore month range for daily chart
    if (yearMonth) {
      const [year, month] = yearMonth.split('-');
      const begin = `${year}-${month}-01T00:00:00`;
      const endDateObj = new Date(Number(year), Number(month), 0);
      const pad = (n: number) => n.toString().padStart(2, '0');
      const end = `${endDateObj.getFullYear()}-${pad(endDateObj.getMonth() + 1)}-${endDateObj.getDate()}T00:00:00`;
      setCustomDateRange({ begin, end });
    } else {
      setCustomDateRange(undefined);
    }
  };

  const moveToMonthly = () => {
    setView('monthly');
    setCustomDateRange(undefined);
  };

  const handleBack = () => {
    if (view === 'halfhourly') {
      moveToDaily();
    } else if (view === 'daily') {
      moveToMonthly();
    }
  };

  // Chart visibility logic
  const canShowChart = useMemo(() => {
    if (selectedCustomer && mpanPeriodState.period) return true;
    return Boolean(mpanPeriodState.mpans.length && mpanPeriodState.period);
  }, [mpanPeriodState, selectedCustomer]);

  const handleOnCustomerChange = async (customer: string): Promise<void> => {
    setMpanPeriodState({ period: '', mpans: [] });

    setSelectedCustomer(customer);

    if (!customer) {
      setMpansForSelectedCustomer([]);
      return;
    }

    const customerId = customer;

    const filters: MetersFilterObject = {
      'site-reference': undefined,
      'mpan-core': undefined,
      'hh-or-nhh': undefined,
    };

    setIsLoading(true);

    var mpanFilterItems: Array<MpanFilterItem> = await loadMpans(authContext, customerId);

    setIsLoading(false);

    setMpansForSelectedCustomer(mpanFilterItems);
  };

  const handleAddMpan = (mpanFilterItem: MpanFilterItem) => {
    setMpanPeriodState(prev => ({
      ...prev,
      mpans: prev.mpans.includes(mpanFilterItem) ? prev.mpans : [...prev.mpans, mpanFilterItem],
    }));
  };

  const handleRemoveMpan = async (mpan: string) => {
    const newMpans = mpanPeriodState.mpans.filter((mpanDetails: MpanFilterItem) => mpanDetails.mpan !== mpan);
    setMpanPeriodState(prev => ({
      ...prev,
      mpans: newMpans,
    }));

    if (newMpans.length === 0) {
      setMpanPeriodState(prev => ({ ...prev, mpans: [] }));

      if (view === 'daily' || view === 'halfhourly') {
        moveToMonthly();
      }
    }
  };

  const handleReset = () => {
    setMpanPeriodState({ period: '', mpans: [] });
    setSelectedCustomer('');
    setView('monthly');
    setYearMonth(undefined);
    setHHDate(undefined);
    setCustomDateRange(undefined);
    setMonthlyCache(null);
    setDailyCache(null);
    setHhCache(null);
  };

  const handleDateChange = (date: SelectDateResult, type: 'Monthly' | 'Daily') => {
    if (!date.begin) return;

    if (type === 'Daily') {
      const ymd = date.begin.format('YYYY-MM-DD');
      handleDayClick(ymd);
      return;
    }

    if (type === 'Monthly') {
      const ym = date.begin.format('YYYY-MM');
      handleMonthClick(ym);
      return;
    }
  };

  const handleCloseMenuClick = (event: React.MouseEvent<HTMLElement>): void => {
    event.preventDefault();
    globalContext.setGlobalDetail({
      ...globalContext.globalDetail,
      mobileFilterActive: false,
    });
  };

  const handleDownloadReport = async (event: any) => {
    const fileType: 'csv' | 'xml' | 'xlsx' = event.value;

    // Call Google Analytics
    const claims = authContext.getClaims();
    const tpiReferenceId = claims.tpiReferenceId;
    const billingPartyId = claims.billingPartyId;

    const tpiUser = isTpiUser();
    const userType = tpiUser ? 'TPIUsers' : 'Customers';

    if (tpiUser) {
      dataLayerPageEvents(tpiReferenceId, userType);
    } else {
      dataLayerPageEvents(billingPartyId, userType);
    }

    const eventMessage = fileType === 'xml' ? 'Download graph data xml' : 'Download graph data Excel';

    // Month, Day, Last 24 months, Last 12 months, Last 6 months).
    let granularity = `Last ${mpanPeriodState.period} months`;
    if (view === 'daily') granularity = 'Month';
    else if (view === 'halfhourly') granularity = 'Day';

    TagManager.pushData({
      event: eventMessage,
      account: selectedCustomer,
      count: mpanPeriodState.mpans.length,
      granularity: granularity,
      userType: userType,
    });
    // End Google Analytics

    let period = '12';
    let mpans = [];
    if (view === 'monthly') {
      let date = getDateRange(mpanPeriodState.period);

      const startDate = date.begin;
      const endDate = date.end;

      // if no mpans then it's customer
      if (mpanPeriodState.mpans.length === 0) {
        const consumptionData = customerMonthlyData.map(consumption => ({
          accountReference: selectedCustomer,
          actualConsumption: consumption.actualConsumption,
          estimatedConsumption: consumption.estimatedConsumption,
          totalConsumption: consumption.totalConsumption,
          intervalEnd: moment(consumption.intervalEnd).format(DATE_FORMAT),
          intervalStart: moment(consumption.intervalStart).format(DATE_FORMAT),
          consumptionSource: consumption.сonsumptionSource,
          groupingInterval: 'Month',
        }));

        const result = await convertDataToReport(fileType, consumptionData, 'Monthly Read Data');
        if (result !== null) {
          const fileName = `${selectedCustomer}-Overview-Elec-Data-${startDate}-${endDate}`;

          // Download file
          if (fileType === 'xlsx') {
            downloadXLSX(result, fileName);
          } else if (fileType === 'xml') {
            downloadXML(result as string, fileName);
          }
        }
      } else {
        // if mpans exist in mpanPeriodState.mpans get data out of monthlyCache
        period = mpanPeriodState.period;
        mpans = mpanPeriodState.mpans;

        let fileName = '';
        let totalArrayToConvert: any[] = [];

        mpans.forEach((mpan: MpanFilterItem) => {
          const cacheItemName = `${mpan.mpan}_${startDate}${endDate}`;
          let item = monthlyCache && monthlyCache[cacheItemName];

          fileName = `${selectedCustomer}-${mpan.mpan}-Elec-Data-${startDate}${endDate ? '-' + endDate : ''}`;

          // Filter out blank data
          const consumptions = item?.consumptions.filter(data => data.сonsumptionSource === mpan.hhOrNhh);

          const consumptionData = consumptions?.map(consumption => ({
            accountReference: selectedCustomer,
            actualConsumption: consumption.actualConsumption,
            estimatedConsumption: consumption.estimatedConsumption,
            totalConsumption: consumption.totalConsumption,
            intervalEnd: moment(consumption.intervalEnd).format(DATE_FORMAT),
            intervalStart: moment(consumption.intervalStart).format(DATE_FORMAT),
            consumptionSource: consumption.сonsumptionSource,
            mpan: mpan.mpan,
            groupingInterval: 'Month',
          }));

          if (consumptionData) {
            totalArrayToConvert = totalArrayToConvert.concat(consumptionData);
          }
        });

        if (totalArrayToConvert) {
          if (mpans.length > 1)
            fileName = `${selectedCustomer}-Multi-Elec-Data-${startDate}${endDate ? '-' + endDate : ''}`;

          const result = await convertDataToReport(fileType, totalArrayToConvert, 'Monthly Read Data');

          if (result !== null) {
            // Download file
            if (fileType === 'xlsx') {
              downloadXLSX(result, fileName);
            } else if (fileType === 'xml') {
              downloadXML(result as string, fileName);
            }
          }
        }
      }
    } else if (view === 'daily') {
      // using customDateRange get data out of dailyCache for mpans in mpanPeriodState.mpans

      let date = getDateRange(view, customDateRange);

      const startDate = date.begin;
      const endDate = date.end;

      // if mpans exist in mpanPeriodState.mpans get data out of monthlyCache
      period = mpanPeriodState.period;
      mpans = mpanPeriodState.mpans;

      let fileName = '';
      let totalArrayToConvert: any[] = [];

      mpans.forEach((mpan: MpanFilterItem) => {
        const cacheItemName = `${mpan.mpan}_${startDate}${endDate}`;
        let item = dailyCache && dailyCache[cacheItemName];

        fileName = `${selectedCustomer}-${mpan.mpan}-Elec-Data-${startDate}${endDate ? '-' + endDate : ''}`;

        const consumptionData = item?.consumptions.map(consumption => ({
          accountReference: selectedCustomer,
          actualConsumption: consumption.actualConsumption,
          estimatedConsumption: consumption.estimatedConsumption,
          totalConsumption: consumption.totalConsumption,
          intervalEnd: moment(consumption.intervalEnd).format(DATE_FORMAT),
          intervalStart: moment(consumption.intervalStart).format(DATE_FORMAT),
          consumptionSource: consumption.сonsumptionSource,
          mpan: mpan.mpan,
          groupingInterval: 'Day',
        }));

        if (consumptionData) {
          totalArrayToConvert = totalArrayToConvert.concat(consumptionData);
        }
      });

      if (totalArrayToConvert) {
        if (mpans.length > 1)
          fileName = `${selectedCustomer}-Multi-Elec-Data-${startDate}${endDate ? '-' + endDate : ''}`;

        const result = await convertDataToReport(fileType, totalArrayToConvert, 'Daily Read Data');

        if (result !== null) {
          // Download file
          if (fileType === 'xlsx') {
            downloadXLSX(result, fileName);
          } else if (fileType === 'xml') {
            downloadXML(result as string, fileName);
          }
        }
      }
    } else if (view === 'halfhourly') {
      // using customDateRange get data out of hhCache for mpans in mpanPeriodState.mpans

      let date = getDateRange(view, customDateRange);

      const startDate = date.begin;
      const endDate = date.end;

      // if mpans exist in mpanPeriodState.mpans get data out of monthlyCache
      period = mpanPeriodState.period;
      mpans = mpanPeriodState.mpans;

      let totalArrayToConvert: any[] = [];

      let fileName = '';

      mpans.forEach((mpan: MpanFilterItem) => {
        const cacheItemName = `${mpan.mpan}_${startDate}${endDate}`;
        let item = hhCache && hhCache[cacheItemName];

        fileName = `${selectedCustomer}-${mpan.mpan}-Elec-${mpan.requestType}-Data-${moment(startDate).format(
          REVERSED_DATE_FORMAT_HYPHEN
        )}`;

        const hhData = item?.consumptions;
        if (hhData) {
          let arrayToConvert: any[] = formatHHData(hhData, selectedCustomer, mpan.mpan, '', startDate);

          if (arrayToConvert) {
            totalArrayToConvert = totalArrayToConvert.concat(arrayToConvert);
          }
        }
      });

      if (totalArrayToConvert) {
        if (mpans.length > 1)
          fileName = `${selectedCustomer}-MultiMPAN-Elec-HH-Data-${moment(startDate).format(
            REVERSED_DATE_FORMAT_HYPHEN
          )}`;

        const result = await convertDataToReport(fileType, totalArrayToConvert, 'HH Read Data');

        if (result !== null) {
          // Download file
          if (fileType === 'xlsx') {
            downloadXLSX(result, fileName);
          } else if (fileType === 'xml') {
            downloadXML(result as string, fileName);
          }
        }
      }
    }
  };

  return (
    <>
      <EnergyUsageWrapper>
        <StyledTitle>Energy usage data</StyledTitle>
        <div className='d-flex-column'>
          <div className='mt-3'>
            You can view your electricity usage at account and individual MPAN level.
            <ol className='font-italic'>
              <li>If you don't have a smart meter, we base some of your readings on estimates.</li>
              <li>Monthly data may not align with the dates of your monthly bills.</li>
            </ol>
            {selectedMpansContainNonHH && (
              <NotificationBox
                showButton={true}
                buttonClassName='button'
                boxText={
                  'One of your selected MPANs is unable to display Daily and Half-Hourly data. Upgrading to a smart meter will allow you insight into your energy usage.'
                }
                buttonText='Details'
                onClick={(event: React.MouseEvent<HTMLAnchorElement>) => {
                  event?.preventDefault();
                  window.open('https://energy.drax.com/smart-meters/', '_blank');
                }}
              ></NotificationBox>
            )}
          </div>
        </div>

        <div className=''>
          <div className='row no-gutters'>
            <div className='d-none d-sm-block col-sm-12 col-md-12 col-lg-6 col-xl-3'>
              <Sidebar
                mpansContainHH={mpansContainHH}
                period={mpanPeriodState.period as PeriodValue}
                onPeriodChange={handlePeriodChange}
                onDateChange={handleDateChange}
                mpans={mpansForSelectedCustomer}
                selectedMpans={mpanPeriodState.mpans}
                onAddMpan={handleAddMpan}
                onRemoveMpan={handleRemoveMpan}
                onReset={handleReset}
                selectedCustomer={selectedCustomer}
                onCustomerChange={handleOnCustomerChange}
                view={view}
                viewByOptions={viewByOptions}
              />
            </div>
            <div className='col-12 col-sm-12 col-md-12 col-lg-6 col-xl-9'>
              <Charts
                period={mpanPeriodState.period}
                selectedMpans={mpanPeriodState.mpans.map((mpanFilterItem: MpanFilterItem) => {
                  return mpanFilterItem.mpan;
                })}
                selectedCustomer={selectedCustomer}
                customerMonthlyData={customerMonthlyData}
                monthlySeries={monthlySeries}
                dailySeries={dailySeries}
                hhSeries={hhSeries}
                canShowChart={canShowChart}
                canDownloadChartData={validData}
                onMonthClick={mpansContainHH ? handleMonthClick : () => {}}
                onDayClick={mpansContainHH ? handleDayClick : () => {}}
                onBack={handleBack}
                view={view}
                yearMonth={yearMonth}
                date={hhDate}
                downloadReport={handleDownloadReport}
              />
            </div>
            {isLoading && <Loading overlay nowait />}
          </div>
        </div>
      </EnergyUsageWrapper>

      <MobileActionsPanel showExport={false} showAddUser={false} showDownload={false} />
      <SideMenuFilter>
        <SidebarMobileWrapper>
          <button className='close-menu-icon' onClick={handleCloseMenuClick}>
            <IcomoonReact iconSet={iconSet} color={colors.trueblack} size={15} icon='cross2' />
          </button>
          <SidebarMobile
            mpansContainHH={mpansContainHH}
            period={mpanPeriodState.period as PeriodValue}
            onPeriodChange={handlePeriodChange}
            onDateChange={handleDateChange}
            mpans={mpansForSelectedCustomer}
            selectedMpans={mpanPeriodState.mpans}
            onAddMpan={handleAddMpan}
            onRemoveMpan={handleRemoveMpan}
            onReset={handleReset}
            selectedCustomer={selectedCustomer}
            onCustomerChange={handleOnCustomerChange}
            view={view}
            downloadReport={handleDownloadReport}
            viewByOptions={viewByOptions}
            selectedViewByIndex={selectedViewByIndex}
          ></SidebarMobile>
        </SidebarMobileWrapper>
      </SideMenuFilter>
    </>
  );
}

export default EnergyUsage;
